<template>
  <ModalForm
    v-model="isModalOpen"
    :data="computedDetails ?? {}"
    :title="$t('pages.onboarding.sole_trader.edit_personal.title')"
    :on-submit="handleForm"
    disable-submit-on-error>
    <p class="subheader">
      {{ $t('pages.onboarding.sole_trader.edit_personal.title') }}
    </p>
    <div class="grid grid-cols-2 gap-24">
      <FormKit
        type="TextPatternInput"
        name="firstName"
        :label="$t('form.label.first_name')"
        :pattern="shortNameRule.pattern"
        :validation="shortNameRule.validation"/>
      <FormKit
        type="TextPatternInput"
        name="lastName"
        :label="$t('form.label.last_name')"
        :pattern="shortNameRule.pattern"
        :validation="shortNameRule.validation"/>
      <FormKit
        type="CountryDropdown"
        name="nationalityIso3"
        :label="$t('form.label.nationality')"
        value-key="shortName"
        :options="ALL_COUNTRIES"
        validation="required"/>
      <FormKit
        type="CountryDropdown"
        name="countryOfResidenceIso3"
        :label="$t('form.label.country_of_residence')"
        value-key="shortName"
        :options="PERMITTED_COUNTRIES"
        validation="required"/>
      <FormKit
        type="DateInput"
        name="dateOfBirth"
        :label="$t('form.label.date_of_birth')"
        :validation="`required|date_before:${new Date(time.eighteenYearsAgo)}`"/>
    </div>
    <p class="subheader">
      {{ $t('pages.onboarding.sole_trader.edit_personal.subtitle.residential_address') }}
    </p>
    <FormKit
      type="group"
      name="residentialAddress">
      <div class="grid grid-cols-2 gap-24">
        <AddressFields
          :country-iso3="{
            label: $t('form.label.country_of_residence'),
          }"/>
      </div>
    </FormKit>
    <p class="subheader">
      {{ $t('pages.onboarding.sole_trader.edit_personal.subtitle.mailing_address') }}
    </p>
    <FormKit
      v-model="isMailingAddressSame"
      type="checkbox"
      :label="$t('pages.onboarding.business.company_details.address.data.same_address')"
      name="isMailingAddressSame"
      @input="resetMailingAddress"/>
    <FormKit
      id="mailingAddress"
      type="group"
      name="mailingAddress">
      <div class="grid grid-cols-2 gap-24">
        <AddressFields v-if="!isMailingAddressSame"/>
      </div>
    </FormKit>
  </ModalForm>
</template>

<script setup lang="ts">
import { reset } from '@formkit/core';
import { computed, nextTick } from 'vue';

import ModalForm from '@/components/modals/ModalForm.vue';
import { ALL_COUNTRIES, PERMITTED_COUNTRIES } from '@/constants/countries';
import AddressFields from '@/modules/onboarding/components/AddressFields.vue';
import { isMailingAddressEmpty } from '@/modules/onboarding/utils';
import { NameFormKitRule } from '@/rules/formkit-rules/Name';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import {
  onboardingAdminSoleTraderStoreActions as actions,
  useOnboardingAdminSoleTraderStore,
} from '@/stores/onboarding/OnboardingAdminSoleTrader';
import { time } from '@/utils';

const isModalOpen = defineModel<boolean>('isModalOpen', { required: true });

const { personalDetails, isMailingAddressSame } = useOnboardingAdminSoleTraderStore();

function getMailingAddressFormValue() {
  const { residentialAddress, mailingAddress } = personalDetails.value || {};
  return !isMailingAddressEmpty(residentialAddress, mailingAddress) ? mailingAddress : {};
}

const computedDetails = computed(() => ({
  ...personalDetails.value,
  mailingAddress: getMailingAddressFormValue(),
}));

function resetMailingAddress(isSameAddress: boolean) {
  if (isSameAddress) {
    reset('mailingAddress', {});
    return;
  }

  nextTick(() => reset('mailingAddress', getMailingAddressFormValue()));
}

const handleForm = async (data: Onboarding.SoleTrader.Details) => {
  await actions.updatePersonalDetails(data, {
    onSuccess: () => {
      isModalOpen.value = false;
    },
  });
};

const shortNameRule = NameFormKitRule(50);
</script>

<style lang="scss" scoped>
.subheader {
  @apply font-semibold text-22;
}
</style>
